"use client"

import DashboardContentWrapper from "@/shared/components/dashboard-content-wrapper"
import Heading from "@/shared/components/ui/typography/heading"
import ProfileForm from "../../../features/profile/components/forms/profile-form"
import ProfileStatCard from "../../../features/profile/components/profile-stat-card"
import ProfileStatCardWithDropdown from "../../../features/profile/components/profile-stat-card-with-dropdown"
import { Calendar, Clock, FileCheck, Users } from "lucide-react"
import React, { useEffect, useState } from "react"
import { getProfile, getVolunteerProfile } from "@/features/profile/api/profile.api"
import { useSession } from "../../../features/auth/context/session.context"
import { Profile } from "@/features/auth/types/auth-actions.type"
import { UserRole } from "@/shared/constants/user-roles"
import ScrollToTopFab from "@/shared/components/scroll-to-top-fab"
import { usePathname, useSearchParams } from "next/navigation"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/shared/components/ui/accordion"

// Calculate days served from the profile's serve periods (servePeriods from volunteers.serveDates)
// Sums up all days from all serve periods, counting only days between arrival and departure (inclusive)
// Only counts days up to the current date (excludes future dates)
function calculateDaysServedFromProfile(profile: Profile | null): number {
    if (!profile || !profile.servePeriods || !Array.isArray(profile.servePeriods) || profile.servePeriods.length === 0) {
        return 0
    }

    let totalDays = 0
    const today = new Date()
    // Normalize today to midnight to avoid timezone issues
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate())

    // Calculate from all serve periods
    profile.servePeriods.forEach((period) => {
        if (period?.from && period?.to) {
            const from = new Date(period.from)
            const to = new Date(period.to)

            if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
                // Normalize to midnight to avoid timezone issues
                const start = new Date(from.getFullYear(), from.getMonth(), from.getDate())
                const end = new Date(to.getFullYear(), to.getMonth(), to.getDate())

                // Only count if the period has started (start date is in the past or today)
                if (start <= todayMidnight) {
                    // Cap the end date to today if it's in the future
                    const effectiveEnd = end > todayMidnight ? todayMidnight : end

                    const diffMs = effectiveEnd.getTime() - start.getTime()
                    if (diffMs >= 0) {
                        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1
                        totalDays += diffDays
                    }
                }
            }
        }
    })

    return totalDays
}

function ProfilePage() {
    const [data, setData] = useState<any>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [hoursWorked, setHoursWorked] = useState<string>("0h")
    const [workScopesWorked, setWorkScopesWorked] = useState<number>(0)
    const [workScopesList, setWorkScopesList] = useState<Array<{ id: number; workScope: string; workScopeDisplay: string | null; completed?: boolean }>>([])
    const [homeownersSupported, setHomeownersSupported] = useState<number>(0)
    const [homeownersList, setHomeownersList] = useState<Array<{ id: number; fullname: string; email?: string; phone?: string }>>([])
    const [daysServed, setDaysServed] = useState<number>(0)
    const [trainingVideosWatched, setTrainingVideosWatched] = useState<number>(0)
    const [completedProfileMessageValue, setCompletedProfileMessageValue] = useState<string>("profile-message")
    const { session } = useSession()
    const pathname = usePathname()
    const searchParams = useSearchParams()

    useEffect(() => {
        const fetchProfile = async () => {
            if (!session?.sub) {
                setLoading(false)
                return
            }
            try {
                // Use volunteer API (same as Update Volunteer) for non-homeowners so Profile reads/writes from volunteers table consistently
                const isHomeowner = session?.role === UserRole.HOMEOWNER
                const res = isHomeowner
                    ? await getProfile()
                    : await getVolunteerProfile(Number(session.sub))
                if (!res.success) {
                    setError(res.message)
                    return
                }

                const profileData = res.data
                setData(profileData)
                // Calculate Days Served from serve periods (servePeriods from volunteers.serveDates)
                const calculatedDays = calculateDaysServedFromProfile(profileData as Profile)
                setDaysServed(calculatedDays)
                // Calculate Hours Served as Days Served * 7
                const calculatedHours = calculatedDays * 7
                setHoursWorked(`${calculatedHours}h`)
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : "Failed to load profile"
                )
            } finally {
                setLoading(false)
            }
        }

        fetchProfile()
    }, [session?.sub, session?.role])

    useEffect(() => {
        const fetchWorkOrdersStats = async () => {
            if (!session?.sub) return

            try {
                const res = await fetch(
                    `/api/profile/work-orders-stats?userId=${session.sub}`
                )
                
                if (!res.ok) {
                    // Fallback: use profile data for training videos if API call fails
                    if (data) {
                        setTrainingVideosWatched(data.trainingVideosWatched || 0)
                    }
                    return
                }

                const result = await res.json()

                if (result.success && result.data) {
                    setWorkScopesWorked(result.data.workScopesWorked || 0)
                    setWorkScopesList(result.data.workScopesList || [])
                    setHomeownersSupported(result.data.homeownersSupported || 0)
                    setHomeownersList(result.data.homeownersList || [])
                    // Only use API for training videos; Days Served comes from serve periods (servePeriods from volunteers.serveDates)
                    setTrainingVideosWatched(result.data.trainingVideosWatched ?? data?.trainingVideosWatched ?? 0)
                } else {
                    console.warn('[ProfilePage] Work orders stats API returned unsuccessful response:', result)
                    // Fallback: use profile data for training videos if API call fails
                    if (data) {
                        setTrainingVideosWatched(data.trainingVideosWatched || 0)
                    }
                }
            } catch (err) {
                console.error("[ProfilePage] Error fetching work orders stats:", err)
                // Fallback: use profile data for training videos if API call fails
                if (data) {
                    setTrainingVideosWatched(data.trainingVideosWatched || 0)
                }
            }
        }

        if (session?.sub && data) {
            fetchWorkOrdersStats()
        }
    }, [session?.sub, data])

    if (loading || error || !data) {
        return (
            <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
                <div className="text-muted-foreground">Loading Profile</div>
            </DashboardContentWrapper>
        )
    }

    // Check if user needs to complete profile (currentStep = 1)
    const needsProfileCompletion = data?.currentStep === 1
    const isHomeowner = session?.role === UserRole.HOMEOWNER

    // Incomplete profiles show a blue prompt. Completed profiles show the green thank-you bar.
    const completionMessage = isHomeowner
        ? "Complete the yellow required fields to unlock ReliefHub navigation. Your Profile helps us know how we can best serve you, and one of our trained assessors will be in touch with you soon. We take your required information seriously and protect it according to our"
        : "Complete the yellow required fields to unlock ReliefHub navigation and access all serve information. Your Profile helps connect your experience and availability with recovery work so you and your team leaders can identify where you can make the most impact. Your Profile also tracks where you've served, including workscopes and homeowners/projects supported. We take your required information seriously and protect it according to our"

    const showGreenBar = !needsProfileCompletion
    const showBlueBar = needsProfileCompletion

    const completedProfileMessage = isHomeowner
        ? "Thank you for completing your profile! We take your required information seriously and protect it according to our"
        : "Thank you for preparing to serve. Completing your Profile helps connect your experience and availability with recovery work, so you and your team leaders can identify where you can make the most impact. Your Profile also tracks where you've served, including workscopes and homeowners/projects supported. We take your required information seriously and protect it according to our"
    const currentProfilePath = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`
    const privacyHref = `/privacy?returnTo=${encodeURIComponent(currentProfilePath)}`

    return (
        <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
            <Heading>Profile</Heading>
            {showBlueBar && (
                <Accordion type="single" defaultValue="profile-message" collapsible>
                    <AccordionItem value="profile-message" className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                        <AccordionTrigger className="py-0 text-blue-900 font-semibold" downChevronIconClassName="w-5 h-5">
                            Complete your Profile to unlock ReliefHub
                        </AccordionTrigger>
                        <AccordionContent className="pb-0 pt-2">
                            <p className="text-blue-900 font-medium">
                                {completionMessage}{" "}
                                <a href={privacyHref} className="underline underline-offset-2 hover:text-blue-800">
                                    Privacy Policy
                                </a>
                                .
                            </p>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            )}
            {showGreenBar && (
                <Accordion
                    type="single"
                    value={completedProfileMessageValue}
                    onValueChange={setCompletedProfileMessageValue}
                    collapsible
                >
                    <AccordionItem value="profile-message" className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                        <AccordionTrigger className="py-0 text-green-900 font-semibold" downChevronIconClassName="w-5 h-5">
                            <h2 className="text-xl font-semibold">Thank You</h2>
                        </AccordionTrigger>
                        <AccordionContent className="pb-0 pt-2">
                            <p className="text-green-900 font-medium">
                                {completedProfileMessage}{" "}
                                <a href={privacyHref} className="underline underline-offset-2 hover:text-green-800">
                                    Privacy Policy
                                </a>
                                .
                            </p>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            )}
            {/* Only show stats section for volunteers, not homeowners */}
            {!isHomeowner && (
                <div className="grid grid-cols-[repeat(auto-fit_,minmax(200px,_1fr))] gap-5">
                    <ProfileStatCard
                        title="Hours Served"
                        value={hoursWorked}
                        icon={<Clock />}
                    />
                    <ProfileStatCard
                        title="Days Served"
                        value={`${daysServed} ${daysServed === 1 ? 'Day' : 'Days'}`}
                        icon={<Calendar />}
                    />
                    <ProfileStatCardWithDropdown
                        title="Workscopes Worked"
                        value={workScopesWorked.toString()}
                        icon={<FileCheck />}
                        dropdownItems={workScopesList}
                    />
                    <ProfileStatCardWithDropdown
                        title="Homeowners/Projects Supported"
                        value={homeownersSupported.toString()}
                        icon={<Users />}
                        dropdownItems={homeownersList}
                    />
                </div>
            )}
            <ProfileForm 
                profile={data} 
                currentStep={data?.currentStep} 
                role={session?.role || UserRole.VOLUNTEER}
                userId={session?.sub ? Number(session.sub) : undefined}
            />
            <ScrollToTopFab />
        </DashboardContentWrapper>
    )
}

export default ProfilePage
